import React, { useContext, useState } from 'react';
import { Box, DialogActions, DialogContent, Paper, Toolbar } from '@mui/material';
import {
  ICompany,
  useAddCompanyToMembershipMutation,
  useCompaniesQuery
} from '../../../generated/graphql';
import DialogBox from '../../Common/Layout/DialogBox';
import CancelButton from '../../Common/Buttons/CancelButton';
import AutocompleteComponent from '../../Common/Fields/AutocompleteComponent';
import { Colors } from '../../../utils/theme';
import { HomeWork } from '@mui/icons-material';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import SaveButton from '../../Common/Buttons/SaveButton';
import InputComponent from '../../Common/Fields/InputComponent';

type IProps = {
  membershipId: number;
  handleClose: () => void;
  addNewCompany: (company: ICompany) => void;
};

const MembershipAddCompany: React.FunctionComponent<IProps> = ({
  handleClose,
  membershipId,
  addNewCompany
}: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [company, setCompany] = useState<ICompany | null>(null);
  const companies = useCompaniesQuery();
  const [addCompanyToMembership, addCompanyToMembershipResult] =
    useAddCompanyToMembershipMutation();

  const handleCompanyChange = (_: unknown, newValue: React.SetStateAction<ICompany | null>) => {
    setCompany(newValue);
  };

  const getStreetValue = () => {
    let result = '';
    if (company && company.main_address) {
      result = company.main_address.street;
    }
    return result;
  };

  const getCityValue = () => {
    let result = '';
    if (company && company.main_address) {
      result = company.main_address.city;
    }
    return result;
  };

  const getCountryValue = () => {
    let result = '';
    if (company && company.main_address) {
      result = company.main_address.country.name;
    }
    return result;
  };

  const getInvoiceToValue = () => {
    let result = '';
    if (company) {
      result = company.main_contacts.map((c) => c?.fullName).join(', ');
    }
    return result;
  };

  const handleSubmit = () => {
    if (company) {
      if (membershipId === Number(company.membership.id)) {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'info',
            text: `${company.name} is already part of ${company.membership.name} membership`
          }
        });
      } else {
        if (company.is_main_member) {
          const membershipCompanies = companies.data?.result.filter(
            (c) => c?.membership.id === company.membership.id
          );
          const membershipMainCompanies = membershipCompanies?.filter(
            (c) => c?.is_main_member === true && c.id !== company.id
          );
          if (membershipMainCompanies?.length === 0) {
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'warning',
                text: `You should select another main company for ${company.membership.name} first`,
                autoHide: 6000
              }
            });
          } else {
            triggerAddConfirmation(company);
          }
        } else {
          triggerAddConfirmation(company);
        }
      }
    }
  };
  const triggerAddConfirmation = (company: ICompany) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        agreeAction: addToMembership,
        params: company,
        text: `Are you sure you want to add ${company.name} to ${state.selectedMembership?.name} membership?`
      }
    });
  };

  const addToMembership = (company: ICompany) => {
    dispatch({
      type: Actions.HideConfirmation
    });
    addCompanyToMembership({
      variables: {
        id: parseInt(company.id),
        membership_id: membershipId
      },
      update(_, { data }) {
        if (data?.result) {
          addNewCompany(data.result as ICompany);
          handleClose();
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: `You have successfully added ${data.result.name} to the list of membership companies`
            }
          });
        }
      }
    }).catch((e) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: e.message,
          autoHide: 6000
        }
      })
    );
  };

  return (
    <DialogBox
      id="add-company-to-membership-dialog"
      title="Add Company"
      width={1400}
      onClose={handleClose}
    >
      <DialogContent>
        <Box sx={{ display: 'grid', padding: '80px 25%', gap: '66px' }}>
          <AutocompleteComponent
            id="company-options"
            data-testid="membership-company-autocomplete"
            label="Choose company"
            options={(companies.data?.result as never[]) || []}
            disabled={addCompanyToMembershipResult.loading}
            values={company}
            onChange={handleCompanyChange}
            getOptionLabel={(option: ICompany) => option?.name as string}
          />
          <Paper
            square
            sx={{
              height: 216,
              boxShadow: '0px 10px 20px ' + Colors.third + '66',
              borderRadius: 2
            }}
          >
            <Toolbar
              sx={{
                height: 60,
                minHeight: 60,
                paddingLeft: 18,
                textAlign: 'left',
                background: Colors.third + '1F 0% 0% no-repeat padding-box',
                borderRadius: 'inherit'
              }}
            >
              <HomeWork style={{ color: Colors.main, marginRight: '6px', marginTop: '-4px' }} />
              {company ? <span style={{ color: Colors.main }}>{company.name}</span> : null}
            </Toolbar>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                padding: '20px',
                gap: '20px'
              }}
            >
              <InputComponent
                id="membership-company-street"
                data-testid="membership-company-street-input-label"
                label="Street"
                value={getStreetValue()}
                disabled={true}
                additionalStyle={{ gridColumn: 'span 2' }}
              />
              <InputComponent
                id="membership-company-city"
                data-testid="membership-company-city-input-label"
                label="City"
                value={getCityValue()}
                disabled={true}
              />
              <InputComponent
                id="membership-company-country"
                data-testid="membership-company-country-input-label"
                label="Country"
                value={getCountryValue()}
                disabled={true}
              />
              <InputComponent
                id="membership-company-invoice-to"
                data-testid="membership-company-invoice-to-input-label"
                label="Invoice to"
                value={getInvoiceToValue()}
                disabled={true}
                additionalStyle={{ gridColumn: 'span 4' }}
              />
            </Box>
          </Paper>
        </Box>
      </DialogContent>
      <Box
        className="centralize"
        sx={{ gridColumn: 'span 3', marginTop: '100px', marginBottom: '40px' }}
      >
        <CancelButton
          id="cancel-add-company-to-membership-button"
          data-testid="cancel-add-company-to-membership-button"
          onClick={handleClose}
          text="cancel"
        />
        <SaveButton
          id="create-add-company-to-membership-button"
          data-testid="create-add-company-to-membership-button"
          disabled={!company || addCompanyToMembershipResult.loading}
          onClick={handleSubmit}
          text="Add"
        />
      </Box>
    </DialogBox>
  );
};

export default MembershipAddCompany;
